import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import API from '@/lib/api';
import { Bell, Check, CheckCheck, X } from 'lucide-react';

export default function NotificationBell({ variant = 'light' }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const panelRef = useRef(null);
    const wsRef = useRef(null);
    const isDark = variant === 'dark';

    const loadNotifications = useCallback(async () => {
        try {
            const [nRes, cRes] = await Promise.all([
                API.get('/notifications/mine?limit=20'),
                API.get('/notifications/unread-count'),
            ]);
            setNotifications(nRes.data);
            setUnreadCount(cRes.data.count);
        } catch (err) { /* not logged in yet */ }
    }, []);

    // Poll for notifications every 15s
    useEffect(() => {
        if (!user) return;
        loadNotifications();
        const interval = setInterval(loadNotifications, 15000);
        return () => clearInterval(interval);
    }, [user, loadNotifications]);

    // Close on click outside
    useEffect(() => {
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const markRead = async (id) => {
        try {
            await API.put(`/notifications/read/${id}`);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) { console.error(err); }
    };

    const markAllRead = async () => {
        try {
            await API.put('/notifications/read-all');
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (err) { console.error(err); }
    };

    const handleClick = (notif) => {
        if (!notif.read) markRead(notif.id);
        if (notif.link) {
            navigate(notif.link);
            setOpen(false);
        }
    };

    if (!user) return null;

    const typeIcons = {
        deal: 'bg-blue-100 text-blue-600',
        payment: 'bg-emerald-100 text-emerald-600',
        dispute: 'bg-amber-100 text-amber-600',
        milestone: 'bg-purple-100 text-purple-600',
        success: 'bg-emerald-100 text-emerald-600',
        warning: 'bg-amber-100 text-amber-600',
        error: 'bg-red-100 text-red-600',
        info: 'bg-zinc-100 text-zinc-600',
    };

    return (
        <div className="relative" ref={panelRef} data-testid="notification-bell">
            <button onClick={() => { setOpen(!open); if (!open) loadNotifications(); }}
                className={`relative p-2 rounded-lg transition-colors ${isDark ? 'text-zinc-300 hover:text-white hover:bg-white/10' : 'text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100'}`}
                data-testid="notification-bell-btn">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] px-1"
                        data-testid="notification-badge">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {open && (
                <div className="absolute right-0 top-full mt-2 w-[340px] bg-white rounded-xl shadow-2xl border border-zinc-200 z-50 overflow-hidden" data-testid="notification-panel">
                    <div className="px-4 py-3 border-b border-zinc-200 flex items-center justify-between">
                        <span className="text-sm font-semibold text-zinc-900 font-['Outfit']">Notifications</span>
                        <div className="flex items-center gap-2">
                            {unreadCount > 0 && (
                                <button onClick={markAllRead} className="text-xs text-emerald-600 hover:underline flex items-center gap-1" data-testid="mark-all-read">
                                    <CheckCheck className="w-3 h-3" /> Mark all read
                                </button>
                            )}
                            <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-600">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <div className="max-h-[360px] overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="py-8 text-center">
                                <Bell className="w-6 h-6 text-zinc-300 mx-auto mb-2" />
                                <p className="text-xs text-zinc-400">No notifications yet</p>
                            </div>
                        ) : notifications.map(n => (
                            <button key={n.id} onClick={() => handleClick(n)}
                                className={`w-full text-left px-4 py-3 border-b border-zinc-100 hover:bg-zinc-50 transition-colors flex items-start gap-3 ${!n.read ? 'bg-emerald-50/50' : ''}`}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${typeIcons[n.type] || typeIcons.info}`}>
                                    <Bell className="w-3.5 h-3.5" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium text-zinc-900 truncate">{n.title}</div>
                                    <div className="text-xs text-zinc-500 line-clamp-2">{n.message}</div>
                                    <div className="text-[10px] text-zinc-400 mt-0.5">{timeAgo(n.created_at)}</div>
                                </div>
                                {!n.read && <div className="w-2 h-2 rounded-full bg-emerald-500 mt-2 shrink-0" />}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function timeAgo(dateStr) {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}
